import { useState } from "react";

import { ApolloError } from "@apollo/client";
import {
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { ModelType } from "../../../API";
import StyledDialog from "../../../common/components/dialog/StyledDialog";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { modelToServiceMap } from "../../../common/variables/common";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useSelectedLocationDropdown } from "../../../common/variables/selectedLocationDropdown";
import { useSyncShadow } from "../../model-manager/hooks/useSyncShadow";
import { configurationParametersByModelType } from "../../model-manager/variables/modelManager";
import { useGetNodes } from "../../nodes/hooks/useGetNodes";
import { useCreateBatchServices } from "../hooks/useCreateBatchServices";
import { useCreateDevice } from "../hooks/useCreateDevice";
import { useSetIsRunningServiceStatus } from "../hooks/useSetIsRunningServiceStatus";

/** Docker image name overrides for model types that differ from the model key */
const DOCKER_IMAGE_MODEL_MAP: Record<string, string> = {
  agg: "gateguard",
  tlm: "tanklevel",
};

/** Available models for the Add Stream dialog */
const STREAM_MODELS = [
  { key: ModelType.liquidleak, label: "Liquid Leak" },
  { key: ModelType.gasleak, label: "Gas Leak" },
  { key: "firesmoke" as ModelType, label: "Fire / Smoke" },
] as const;

const parseIpFromRtsp = (url: string): string => {
  const match = url.match(/rtsp:\/\/(?:.*@)?(\d+\.\d+\.\d+\.\d+)/);

  return match?.[1] ?? "0.0.0.0";
};

interface AddStreamDialogProps {
  open: boolean;
  onClose: () => void;
}

const AddStreamDialog = ({
  open,
  onClose,
}: AddStreamDialogProps): JSX.Element => {
  const customerId = useCustomerIdGuard();
  const selectedLocation = useSelectedLocationDropdown();
  const { syncShadowsForNode } = useSyncShadow();
  const { createDevice, loading: deviceLoading } = useCreateDevice();
  const { batchCreateServices, loading: servicesLoading } =
    useCreateBatchServices();
  const { setIsRunningServiceStatus } = useSetIsRunningServiceStatus();
  // const COMPUTE_LOCATION_ID = `${customerId}#L#office`;
  const { data: nodesData, loading: nodesLoading } = useGetNodes();
  // {locationId: COMPUTE_LOCATION_ID,} // 10/23/2026 currently on ccai we do not support added locationid and must use selctedLocation value from dropdown. Should be changed soon when dev is merged in

  const [streamName, setStreamName] = useState("");
  const [rtspUrl, setRtspUrl] = useState("");
  const [selectedModels, setSelectedModels] = useState<string[]>([
    ModelType.liquidleak,
  ]);

  const loading = deviceLoading || servicesLoading;

  // Auto-select specific compute node (hardcoded for now)
  const availableNodes = nodesData?.getNodes?.items ?? [];
  const autoNode = availableNodes[0];
  // TODO: expand on autoNode: decide and build on compute location and how to select a node from that group
  // const autoNode = availableNodes.find(
  //   node => node?.nodeName === "test field agxorin-64gb1"
  // );

  const handleModelToggle = (modelKey: string) => {
    setSelectedModels(prev =>
      prev.includes(modelKey)
        ? prev.filter(m => m !== modelKey)
        : [...prev, modelKey]
    );
  };

  const resetForm = () => {
    setStreamName("");

    setRtspUrl("");

    setSelectedModels([ModelType.liquidleak]);
  };

  const handleClose = () => {
    resetForm();

    onClose();
  };

  const handleSubmit = async () => {
    // Validation
    if (!streamName.trim()) {
      errorNotification("Stream name is required");

      return;
    }

    if (!streamName.match(/^[a-zA-Z0-9_.-]+$/)) {
      errorNotification(
        "Stream name can only contain letters, numbers, dashes, underscores, and dots"
      );

      return;
    }

    if (!rtspUrl.trim().startsWith("rtsp://")) {
      errorNotification("RTSP URL must start with rtsp://");

      return;
    }

    if (selectedModels.length === 0) {
      errorNotification("Select at least one model");

      return;
    }

    if (!autoNode) {
      errorNotification(
        "No compute nodes available. Contact your administrator."
      );

      return;
    }

    if (!selectedLocation?.value) {
      errorNotification("Please select a facility location first");

      return;
    }

    try {
      const parsedIp = parseIpFromRtsp(rtspUrl);

      const nodeId =
        autoNode.nodeId
          ?.replaceAll("C_", "C#")
          ?.replaceAll("_L_", "#L#")
          ?.replaceAll("_N_", "#N#") ?? "";
      const nodeIP = autoNode.nodeIP ?? "";

      // 1. Create device
      await createDevice({
        customerId,
        locationId: selectedLocation.value,
        locationName: selectedLocation.title,
        name: streamName.trim(),
        nodeId,
        nodeName: autoNode.nodeName,
        makeModelId: "RTSP-Stream",
        cameraIpAddress: parsedIp,
        deviceData: JSON.stringify({
          sourceVideo: rtspUrl.trim(),
          rtspHost: `rtsp://${nodeIP}/${streamName.trim()}`,
          hasPanTilt: false,
          hasRecordVideo: true,
          hasZoom: false,
          protocol: "tcp",
        }),
      });

      // 2. Create services for selected models
      const serviceInputs = selectedModels.map(modelType => {
        const serviceType = modelToServiceMap[modelType as ModelType];
        const dockerImageModel = DOCKER_IMAGE_MODEL_MAP[modelType] || modelType;
        const baseConfig =
          (configurationParametersByModelType[modelType] as Record<
            string,
            unknown
          >) ?? {};

        return {
          serviceType,
          nodeId,
          deviceId: `DE#${streamName.trim()}`,
          locationId: selectedLocation.value,
          customerId,
          configuration: JSON.stringify({
            docker_image: `534256877503.dkr.ecr.us-east-1.amazonaws.com/ccai-${dockerImageModel}:latest`,
            ...baseConfig,
          }),
        };
      });

      const servicesResult = await batchCreateServices(serviceInputs);

      // 3. Auto-start each service
      const createdServices = servicesResult?.data?.batchAddServices ?? [];

      for (const service of createdServices) {
        if (service?.id) {
          await setIsRunningServiceStatus(
            {
              runStatus: true,
              nodeId,
              serviceId: service.id,
            },
            ""
          );
        }
      }

      // 4. Sync node shadows
      await syncShadowsForNode(nodeId);

      handleClose();

      successNotification("Stream added successfully! AI models are starting.");
    } catch (error) {
      if (
        (error as ApolloError).message?.includes(
          "The conditional request failed"
        )
      ) {
        errorNotification("A device with this name already exists");
      } else {
        errorNotification(
          (error as ApolloError).message ?? "Failed to add stream"
        );
      }

      console.error(error);
    }
  };

  return (
    <StyledDialog
      open={open}
      title="Add Stream"
      onClose={handleClose}
      maxWidth="sm"
      SubmitButtonProps={{
        loading,
        title: "Add Stream",
        onSubmit: handleSubmit,
      }}
    >
      <Stack spacing={3} sx={{ mt: 1 }}>
        {!nodesLoading && !autoNode && (
          <Alert severity="warning">
            No compute nodes available. Please ensure nodes are set up at the
            compute location before adding streams.
          </Alert>
        )}

        {!selectedLocation?.value && (
          <Alert severity="info">
            Select a facility location from the dropdown above before adding a
            stream.
          </Alert>
        )}

        <TextField
          label="Stream Name"
          placeholder="e.g., tank-farm-cam-1"
          value={streamName}
          onChange={e => setStreamName(e.target.value)}
          fullWidth
          required
          helperText="Letters, numbers, dashes, underscores, and dots only"
          inputProps={{ maxLength: 30 }}
        />

        <TextField
          label="RTSP URL"
          placeholder="rtsp://user:password@192.168.1.100:554/stream1"
          value={rtspUrl}
          onChange={e => setRtspUrl(e.target.value)}
          fullWidth
          required
          helperText="The full RTSP URL for the camera stream"
        />

        <div>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            AI Models
          </Typography>
          <FormGroup>
            {STREAM_MODELS.map(({ key, label }) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={selectedModels.includes(key)}
                    onChange={() => handleModelToggle(key)}
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
        </div>
      </Stack>
    </StyledDialog>
  );
};

export default AddStreamDialog;
